import React, { useContext, Fragment, useEffect } from "react";
import { SurveyContext, QuestionProps } from "../survey";
import {
  PostSessionSurveyCareerQuestionDocument,
  PostSessionSurveyCareerQuestionQuery,
} from "../../generated/graphql";
import TextArea from "../../components/data_entry/text_area";
import { gql } from "@urql/core";
import QuestionLabel from "./components/question_label";
import useRotatingQuestionIndex from "./hooks/useRotatingQuestion";
import { useQuery } from "urql";
import { captureError } from "../../../utils/capture_error";

function availableMentorQuestions({
  studentFirstName,
  studentGoal,
  getGeneric = false,
}: {
  studentFirstName: string;
  studentGoal?: string;
  getGeneric?: boolean;
}) {
  const questions = [
    {
      prompt:
        "Is there anything else you'd like to share about your mentee? For example, important questions they asked, things they are really excited/confused/nervous about, or advice you have for the school to support them better.",
      qid: "rotating_career_mentor_anything_else",
    },
    {
      prompt: `Please share one way in which you have helped ${
        getGeneric ? "{studentName}" : studentFirstName
      } be more successful.`,
      qid: "rotating_career_mentor_success",
    },
    {
      prompt: `How has participating in this mentorship program impacted your relationship with your institution?`,
      qid: "rotating_career_mentor_impact",
    },
  ];
  // we only want to include the student goal question if the student has one in their profile
  if (studentGoal) {
    questions.splice(2, 0, {
      prompt: `At the beginning of this program, we asked ${
        getGeneric ? "{studentName}" : studentFirstName
      } to choose a top goal for the year: secure an internship, learn more about career options, do well in courses, or a write-in answer. They chose: "${
        getGeneric ? "{goal}" : studentGoal
      }". How have you helped ${
        getGeneric ? "{studentName}" : studentFirstName
      } make progress towards this goal, and/or what difficulties have you encountered along the way?`,
      qid: "rotating_career_mentor_goal",
    });
  }
  return questions;
}

function availableStudentQuestions({
  mentorFirstName,
  studentGoal,
  getGeneric = false,
}: {
  mentorFirstName: string;
  studentGoal?: string;
  getGeneric?: boolean;
}) {
  const questions = [
    {
      prompt:
        "Is there anything you'd like to share with your school about your mentorship? For example, something you're excited or worried about, or a goal you've set.",
      qid: "rotating_career_mentee_anything_else",
    },
    {
      prompt: `What is one question ${
        getGeneric ? "{mentorName}" : mentorFirstName
      } has helped you answer? How did they help you answer it?`,
      qid: "rotating_career_mentee_success",
    },
    {
      prompt: `How has participating in this mentorship program impacted your relationship with your institution?`,
      qid: "rotating_career_mentee_impact",
    },
  ];
  // we only want to include the student goal question if the student has one in their profile
  if (studentGoal) {
    questions.splice(2, 0, {
      prompt: `At the beginning of this program, we asked you to choose a top goal for the year: secure an internship, learn more about career options, do well in courses, or a write-in answer. You chose: "${
        getGeneric ? "{goal}" : studentGoal
      }". How has ${
        getGeneric ? "{mentorName}" : mentorFirstName
      } helped you make progress towards this goal, and/or what difficulties have you encountered along the way?`,
      qid: "rotating_career_mentee_goal",
    });
  }
  return questions;
}

gql`
  query PostSessionSurveyCareerQuestion($participantId: ID!, $mentorshipId: ID!) {
    participant(id: $participantId) {
      id
      __typename
      program {
        id
        programSequence {
          id
          school {
            id
            name
          }
        }
      }
    }
    mentorship(id: $mentorshipId) {
      id
      student {
        id
        user {
          id
          firstName
          lastName
        }
        programGoal
      }
      mentor {
        id
        user {
          id
          firstName
          lastName
        }
      }
    }
  }
`;

export interface Answer {
  prompt: {
    generic: string;
    actual: string;
    qid: string;
  };
  answer: string;
}

export interface Customizations {
  optional?: boolean;
}

interface Props extends QuestionProps<Answer, Customizations> {
  participant: PostSessionSurveyCareerQuestionQuery["participant"];
  mentorship: PostSessionSurveyCareerQuestionQuery["mentorship"];
}

export const getRotatingCareerOpenResponseQuestions = (
  participant: PostSessionSurveyCareerQuestionQuery["participant"],
  mentorship: PostSessionSurveyCareerQuestionQuery["mentorship"],
) =>
  participant.__typename === "Mentor"
    ? availableMentorQuestions({
        studentFirstName: mentorship.student.user.firstName,
        studentGoal: mentorship.student.programGoal ? mentorship.student.programGoal : undefined,
      })
    : availableStudentQuestions({
        mentorFirstName: mentorship.mentor.user.firstName,
        studentGoal: mentorship.student.programGoal ? mentorship.student.programGoal : undefined,
      });

function RotatingCareerOpenResponseInput({
  currentAnswer,
  updateAnswer,
  participant,
  mentorship,
  setAbleToSubmit,
  customizations,
}: Props) {
  const rotatingQuestions = getRotatingCareerOpenResponseQuestions(participant, mentorship);
  const genericRotatingQuestions =
    participant.__typename === "Mentor"
      ? availableMentorQuestions({
          studentFirstName: mentorship.student.user.firstName,
          studentGoal: mentorship.student.programGoal ? mentorship.student.programGoal : undefined,
          getGeneric: true,
        })
      : availableStudentQuestions({
          mentorFirstName: mentorship.mentor.user.firstName,
          studentGoal: mentorship.student.programGoal ? mentorship.student.programGoal : undefined,
          getGeneric: true,
        });
  const { fetching, questionIndex } = useRotatingQuestionIndex({
    numberOfQuestions: rotatingQuestions.length,
  });
  const optional =
    !customizations || typeof customizations.optional === "undefined"
      ? false
      : customizations.optional;
  const answerIsValid = optional || (currentAnswer?.answer?.length || 0) > 0;
  useEffect(() => {
    setAbleToSubmit(answerIsValid);
  }, [answerIsValid, setAbleToSubmit]);

  if (fetching || questionIndex === null) return <div>Loading...</div>;
  if (typeof questionIndex === "undefined") {
    captureError(new Error(`Question index is undefined`));
    throw new Error("Question index is undefined");
  }
  const question = rotatingQuestions[questionIndex];
  const genericQuestion = genericRotatingQuestions[questionIndex];

  return (
    <Fragment>
      <QuestionLabel
        data-test="rotating-career-question-prompt"
        label={question.prompt}
        questionIsOptional={optional}
      />
      <TextArea
        data-test="rotating-career-question-input"
        rows={3}
        questionIsOptional={optional}
        onChange={({ currentTarget: { value: updatedResponse } }) =>
          updateAnswer({
            prompt: { generic: genericQuestion.prompt, actual: question.prompt, qid: question.qid },
            answer: updatedResponse,
          })
        }
        value={currentAnswer ? currentAnswer.answer : ""}
      />
    </Fragment>
  );
}

export default function RotatingCareerOpenResponse(
  props: QuestionProps<Answer, { optional?: boolean }>,
) {
  const { participantId, mentorshipId } = useContext(SurveyContext);
  if (!mentorshipId) {
    captureError(new Error(`Error: missing mentorshipId for RotatingCareerOpenResponse`));
    throw new Error("Missing mentorship id");
  }
  const [{ fetching, error, data }] = useQuery({
    query: PostSessionSurveyCareerQuestionDocument,
    variables: { participantId, mentorshipId },
  });
  if (fetching || data === null) return <div>Loading...</div>;
  if (error || !data) {
    const graphQLErrorsForAPM = error?.graphQLErrors?.join("; ") ?? "";
    captureError(
      new Error(`Error fetching usePostSessionSurveyCareerQuestionQuery: ${graphQLErrorsForAPM}`),
    );
    return null;
  }

  const { participant, mentorship } = data;
  return (
    <div className="multi-select">
      <RotatingCareerOpenResponseInput
        {...props}
        participant={participant}
        mentorship={mentorship}
      />
    </div>
  );
}
